import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { currencyFormat } from "../helpers.js";
import { summarizeTeam } from "../data/companyTeamDemo.js";
import {
  applyAllocationDefaults,
  calculateAllocatedRd,
  DEFAULT_MARKET_SALARY,
  suggestRdPct,
} from "../utils/companyTeamAllocation.js";
import { focusCompanyProfileField } from "../utils/companyProfileSearch.js";

const companiesTableId = process.env.REACT_APP_AIR_COMPANIES;
const airtableBaseId = process.env.REACT_APP_AIR_BASEID;
const airtableApiKey = process.env.REACT_APP_AIR_KEY;

export function buildLinkedInPeopleUrl(linkedInUrl) {
  if (!linkedInUrl) return "";
  const base = String(linkedInUrl).trim().replace(/\/+$/, "");
  return /\/people\/?$/i.test(base) ? `${base}/` : `${base}/people/`;
}

export function normalizeLinkedInCompanyUrl(url) {
  const trimmed = String(url || "").trim();
  if (!trimmed) return "";
  let href = trimmed;
  if (!/^https?:\/\//i.test(href)) href = `https://${href}`;
  return href.replace(/\/people\/?$/i, "").replace(/\/+$/, "");
}

export function isValidLinkedInCompanyUrl(url) {
  const normalized = normalizeLinkedInCompanyUrl(url);
  return /linkedin\.com\/company\/[^/?#]+/i.test(normalized);
}

async function patchCompanyFields(companyId, fields) {
  const response = await fetch(
    `https://api.airtable.com/v0/${airtableBaseId}/${companiesTableId}/${companyId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${airtableApiKey}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fields }),
    }
  );

  if (!response.ok) {
    let detail = "";
    try {
      detail = JSON.stringify(await response.json());
    } catch {
      detail = await response.text();
    }
    throw new Error(`Could not save company URL (${response.status}): ${detail}`);
  }

  return response.json();
}

export async function requestCompanyTeam(url, options = {}) {
  let response;
  try {
    response = await fetch(url, options);
  } catch (error) {
    if (error.name === "AbortError") throw error;
    throw new Error(
      `Could not reach ${url}. Is the API server running? (${error.message})`
    );
  }

  const rawBody = await response.text();
  let data = null;
  try {
    data = rawBody ? JSON.parse(rawBody) : null;
  } catch {
    const snippet = rawBody.trim().slice(0, 200);
    throw new Error(
      `${url} returned ${response.status} ${response.statusText} with a non-JSON body: ${
        snippet || "(empty response)"
      }`
    );
  }

  if (!response.ok) {
    const detail = [data?.error, data?.message].filter(Boolean).join(" — ");
    throw new Error(
      `${url} failed with ${response.status} ${response.statusText}${
        detail ? `: ${detail}` : ""
      }`
    );
  }

  return data || {};
}

const TEAM_INDEX_POLL_INITIAL_MS = 20_000;
const TEAM_INDEX_POLL_INTERVAL_MS = 30_000;
const TEAM_INDEX_POLL_MAX_MS = 10 * 60 * 1000;

export function useCompanyTeamAllocation(company, options = {}) {
  const { inlineTeamSourceEdit = false, onCompanyUpdate } = options;
  const [filter, setFilter] = useState("all");
  const [members, setMembers] = useState([]);
  const [companyInCanada, setCompanyInCanada] = useState(false);
  const [domain, setDomain] = useState("");
  const [sharedCompany, setSharedCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isIndexing, setIsIndexing] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [isEditingTeamSource, setIsEditingTeamSource] = useState(false);
  const [teamSourceDraft, setTeamSourceDraft] = useState("");
  const [isSavingTeamSource, setIsSavingTeamSource] = useState(false);
  const [isPollingForTeam, setIsPollingForTeam] = useState(false);
  const saveTimersRef = useRef({});
  const pendingPatchesRef = useRef({});
  const inFlightSavesRef = useRef(new Set());
  const membersRef = useRef([]);
  const persistPersonRef = useRef(null);
  const teamPollTimeoutRef = useRef(null);
  const teamPollAbortRef = useRef(null);
  const teamPollStartedAtRef = useRef(0);
  const teamPollBaselineCountRef = useRef(0);
  const reestimatePeopleRef = useRef(null);
  const isEstimatingRef = useRef(false);

  membersRef.current = members;

  const hydrateMembers = useCallback(
    (rawMembers, inCanada) =>
      (rawMembers || []).map((person) =>
        applyAllocationDefaults(person, { companyInCanada: inCanada })
      ),
    []
  );

  const loadMembers = useCallback(
    async ({ signal, isManual = false, isPoll = false } = {}) => {
      if (!company?.id) {
        setMembers([]);
        setLoadError("This company has no Airtable record id, so people cannot be looked up.");
        setIsLoading(false);
        return { addedCount: 0 };
      }

      const url = `/api/companyTeam?companyId=${encodeURIComponent(company.id)}`;
      if (isManual && !isPoll) {
        setIsRefreshing(true);
        setActionMessage({ type: "info", text: "Loading indexed people from Airtable…" });
      } else if (!isPoll) {
        setIsLoading(true);
      }
      if (!isPoll) setLoadError("");

      try {
        const data = await requestCompanyTeam(url, { signal });
        const inCanada = Boolean(data.companyInCanada);
        const nextMembers = hydrateMembers(data.members, inCanada);
        setCompanyInCanada(inCanada);
        setDomain(data.domain || "");
        setSharedCompany({
          name: data.companyName || "",
          logoUrl: data.logoUrl || "",
        });

        let roster = nextMembers;
        let addedCount = 0;
        let addedIds = [];
        if (isManual || isPoll) {
          const existing = membersRef.current;
          const existingIds = new Set(existing.map((person) => person.id));
          const added = nextMembers.filter((person) => !existingIds.has(person.id));
          addedCount = added.length;
          addedIds = added.map((person) => person.id);
          roster = [
            ...existing.map((person) =>
              person.salary == null || person.needsSalaryBackfill
                ? applyAllocationDefaults(person, { companyInCanada: inCanada })
                : person
            ),
            ...added,
          ];
          setMembers(roster);

          if (!isPoll) {
            const addedRd = added.filter((person) => person.isRd).length;
            setActionMessage({
              type: "success",
              text: added.length
                ? `Added ${added.length} new ${
                    added.length === 1 ? "person" : "people"
                  } from ${data.domain || "this domain"}${
                    addedRd ? ` (${addedRd} likely R&D)` : ""
                  }. Existing rows kept.`
                : nextMembers.length
                ? `No new people found for ${
                    data.domain || "this domain"
                  }. Roster is already up to date.`
                : `No people in Airtable matched ${
                    data.domain || "this company's domain"
                  } yet. Use Index team or Add person.`,
            });
          } else if (added.length) {
            setActionMessage({
              type: "info",
              text: `Found ${added.length} new ${
                added.length === 1 ? "person" : "people"
              } from Clay. Estimating salary and % R&D…`,
            });
          }
        } else {
          setMembers(nextMembers);
        }

        const toBackfill = roster.filter((person) => person.needsSalaryBackfill);
        if (toBackfill.length && persistPersonRef.current && !signal?.aborted && !isPoll) {
          setActionMessage({
            type: "info",
            text: `Saving default ${currencyFormat(DEFAULT_MARKET_SALARY)} market salary for ${
              toBackfill.length
            } ${toBackfill.length === 1 ? "person" : "people"}…`,
          });
          let saved = 0;
          for (const person of toBackfill) {
            if (signal?.aborted) break;
            const ok = await persistPersonRef.current(person.id, {
              salary: DEFAULT_MARKET_SALARY,
              salarySource: "default",
            });
            if (ok) saved += 1;
          }
          if (!signal?.aborted) {
            setMembers((prev) =>
              prev.map((row) =>
                row.needsSalaryBackfill
                  ? { ...row, needsSalaryBackfill: false, salarySource: "default" }
                  : row
              )
            );
            setActionMessage({
              type: "success",
              text: `Saved default ${currencyFormat(DEFAULT_MARKET_SALARY)} salary for ${saved} ${
                saved === 1 ? "person" : "people"
              }. Edit any row to override.`,
            });
          }
        }

        return { addedCount, addedIds };
      } catch (error) {
        if (error.name === "AbortError") return { addedCount: 0, addedIds: [], aborted: true };
        if (!isPoll) {
          setLoadError(error.message);
          setMembers([]);
          if (isManual) setActionMessage(null);
        }
        return { addedCount: 0, addedIds: [], error: true };
      } finally {
        if (!signal?.aborted && !isPoll) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [company?.id, hydrateMembers]
  );

  const stopTeamIndexPolling = useCallback(() => {
    if (teamPollTimeoutRef.current) {
      clearTimeout(teamPollTimeoutRef.current);
      teamPollTimeoutRef.current = null;
    }
    teamPollAbortRef.current?.abort();
    teamPollAbortRef.current = null;
    setIsPollingForTeam(false);
  }, []);

  const runTeamIndexPoll = useCallback(async () => {
    if (!company?.id) return;

    const elapsed = Date.now() - teamPollStartedAtRef.current;
    if (elapsed >= TEAM_INDEX_POLL_MAX_MS) {
      stopTeamIndexPolling();
      setActionMessage({
        type: "info",
        text: "Auto-refresh stopped — Clay may still be writing people. Use Refresh to check again.",
      });
      return;
    }

    const attempt = Math.floor(elapsed / TEAM_INDEX_POLL_INTERVAL_MS) + 1;
    setActionMessage({
      type: "info",
      text: `Waiting for Clay to write people to Airtable… checking automatically (${attempt}).`,
    });

    const controller = new AbortController();
    teamPollAbortRef.current = controller;
    const result = await loadMembers({
      signal: controller.signal,
      isManual: true,
      isPoll: true,
    });
    teamPollAbortRef.current = null;

    if (result?.aborted) return;

    const newTotal = membersRef.current.length;
    if (result.addedCount > 0 || newTotal > teamPollBaselineCountRef.current) {
      stopTeamIndexPolling();
      if (result.addedIds?.length) {
        await reestimatePeopleRef.current?.(result.addedIds);
      }
      return;
    }

    teamPollTimeoutRef.current = setTimeout(runTeamIndexPoll, TEAM_INDEX_POLL_INTERVAL_MS);
  }, [company?.id, loadMembers, stopTeamIndexPolling]);

  const startTeamIndexPolling = useCallback(() => {
    stopTeamIndexPolling();
    teamPollBaselineCountRef.current = membersRef.current.length;
    teamPollStartedAtRef.current = Date.now();
    setIsPollingForTeam(true);
    setActionMessage({
      type: "info",
      text: "Team indexing started — we'll check for new people automatically in about 20 seconds.",
    });
    teamPollTimeoutRef.current = setTimeout(runTeamIndexPoll, TEAM_INDEX_POLL_INITIAL_MS);
  }, [runTeamIndexPoll, stopTeamIndexPolling]);

  useEffect(() => {
    const controller = new AbortController();
    loadMembers({ signal: controller.signal });
    const timers = saveTimersRef.current;
    return () => {
      controller.abort();
      stopTeamIndexPolling();
      Object.values(timers).forEach((timer) => clearTimeout(timer));
    };
  }, [loadMembers, stopTeamIndexPolling]);

  const persistPerson = useCallback(
    async (personId, patch) => {
      if (!company?.id || !personId) return false;
      setSavingId(personId);
      try {
        const current = membersRef.current.find((row) => row.id === personId);
        const payload = {
          companyId: company.id,
          personId,
          name: current?.name,
          title: current?.title,
          salary: current?.salary,
          inCanada: current?.inCanada,
          employmentType: current?.employmentType,
          rdPct: current?.rdPct,
          salarySource: current?.salarySource,
          linkedInWebsite: current?.linkedInWebsite,
          email: current?.email,
          ...patch,
        };
        const data = await requestCompanyTeam("/api/companyTeam", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (data.member) {
          setMembers((prev) =>
            prev.map((row) =>
              row.id === personId
                ? applyAllocationDefaults({ ...row, ...data.member }, { companyInCanada })
                : row
            )
          );
        }
        if (!Object.keys(pendingPatchesRef.current).length) setHasUnsavedEdits(false);
        return true;
      } catch (error) {
        setActionMessage({ type: "error", text: error.message });
        return false;
      } finally {
        setSavingId((current) => (current === personId ? null : current));
      }
    },
    [company?.id, companyInCanada]
  );
  persistPersonRef.current = persistPerson;

  const trackSave = useCallback((promise) => {
    inFlightSavesRef.current.add(promise);
    promise.finally(() => inFlightSavesRef.current.delete(promise));
    return promise;
  }, []);

  const flushEdits = useCallback(async () => {
    Object.values(saveTimersRef.current).forEach((timer) => clearTimeout(timer));
    saveTimersRef.current = {};

    const results = await Promise.all([...inFlightSavesRef.current]);

    const pending = pendingPatchesRef.current;
    pendingPatchesRef.current = {};
    for (const [personId, patch] of Object.entries(pending)) {
      results.push(await trackSave(persistPerson(personId, patch)));
    }

    return results.every((ok) => ok !== false);
  }, [persistPerson, trackSave]);

  useEffect(() => {
    return () => {
      flushEdits();
    };
  }, [flushEdits]);

  const handleFieldChange = useCallback(
    (personId, patch) => {
      let patchToSave = { ...patch };

      setMembers((prev) =>
        prev.map((row) => {
          if (row.id !== personId) return row;
          const next = { ...row, ...patch };
          if (Object.prototype.hasOwnProperty.call(patch, "title")) {
            const prevSuggested = suggestRdPct(row.title);
            if (Number(row.rdPct) === prevSuggested && patch.rdPct == null) {
              next.rdPct = suggestRdPct(patch.title);
              patchToSave = { ...patchToSave, rdPct: next.rdPct };
            }
          }
          return applyAllocationDefaults(next, { companyInCanada });
        })
      );

      pendingPatchesRef.current[personId] = {
        ...(pendingPatchesRef.current[personId] || {}),
        ...patchToSave,
      };

      if (saveTimersRef.current[personId]) {
        clearTimeout(saveTimersRef.current[personId]);
      }
      setHasUnsavedEdits(true);
      saveTimersRef.current[personId] = setTimeout(() => {
        const merged = pendingPatchesRef.current[personId];
        delete pendingPatchesRef.current[personId];
        if (merged) trackSave(persistPerson(personId, merged));
      }, 500);
    },
    [companyInCanada, persistPerson, trackSave]
  );

  const handleIndexTeam = async () => {
    if (!company?.id || isIndexing) return;
    const domainLabel = domain || company?.fields?.Domain || "this company";
    setIsIndexing(true);
    setActionMessage({
      type: "info",
      text: `Sending ${domainLabel} to the Clay team-indexing workflow…`,
    });

    try {
      const data = await requestCompanyTeam("/api/companyTeam", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: company.id, action: "index" }),
      });
      setActionMessage({
        type: "success",
        text:
          data.message ||
          `Team indexing started for ${domainLabel}. Checking for new people automatically…`,
      });
      startTeamIndexPolling();
    } catch (error) {
      setActionMessage({ type: "error", text: error.message });
    } finally {
      setIsIndexing(false);
    }
  };

  const reestimatePeople = useCallback(
    async (personIds) => {
      if (!company?.id || isEstimatingRef.current) return;
      const ids = Array.isArray(personIds) ? personIds.filter(Boolean) : null;
      const count = ids ? ids.length : membersRef.current.length;
      if (!count) return;

      isEstimatingRef.current = true;
      setIsEstimating(true);
      setActionMessage({
        type: "info",
        text: `Asking AI to estimate market salary and % R&D for ${count} ${
          count === 1 ? "person" : "people"
        }…`,
      });

      try {
        const data = await requestCompanyTeam("/api/companyTeam", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            companyId: company.id,
            action: "reestimate",
            ...(ids ? { personIds: ids } : {}),
          }),
        });
        if (Array.isArray(data.members)) {
          setMembers(hydrateMembers(data.members, companyInCanada));
        }
        setActionMessage({
          type: data.failures && data.failures.length ? "error" : "success",
          text:
            data.message ||
            "Re-estimated market salary and % R&D from titles and locations.",
        });
      } catch (error) {
        setActionMessage({ type: "error", text: error.message });
      } finally {
        isEstimatingRef.current = false;
        setIsEstimating(false);
      }
    },
    [company?.id, companyInCanada, hydrateMembers]
  );
  reestimatePeopleRef.current = reestimatePeople;

  const handleReestimate = () => reestimatePeople();

  const handleAddPerson = async () => {
    if (!company?.id || isCreating) return;
    setIsCreating(true);
    setActionMessage({ type: "info", text: "Creating person…" });
    try {
      const data = await requestCompanyTeam("/api/companyTeam", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyId: company.id,
          action: "create",
          name: "New teammate",
          title: "",
          inCanada: companyInCanada,
          employmentType: "employee",
          rdPct: 0,
        }),
      });
      const member = applyAllocationDefaults(data.member, { companyInCanada });
      setMembers((prev) => [member, ...prev]);
      setActionMessage({ type: "success", text: "Person added. Fill in title, salary, and % R&D." });
    } catch (error) {
      setActionMessage({ type: "error", text: error.message });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async (person) => {
    if (!company?.id || !person?.id) return;
    const label = person.name || "this person";
    if (!window.confirm(`Delete ${label} from the people table?`)) return;

    setSavingId(person.id);
    try {
      await requestCompanyTeam("/api/companyTeam", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyId: company.id, personId: person.id }),
      });
      setMembers((prev) => prev.filter((row) => row.id !== person.id));
      setActionMessage({ type: "success", text: `Deleted ${label}.` });
    } catch (error) {
      setActionMessage({ type: "error", text: error.message });
    } finally {
      setSavingId(null);
    }
  };

  const summary = useMemo(() => summarizeTeam(members, []), [members]);

  const allocationSummary = useMemo(() => {
    const canadaCount = members.filter((m) => m.inCanada).length;
    const employeeCount = members.filter((m) => m.employmentType !== "contractor").length;
    const contractorCount = members.length - employeeCount;
    let allocatedTotal = 0;
    let salaryTotal = 0;
    members.forEach((m) => {
      const amount =
        m.allocatedRd != null
          ? m.allocatedRd
          : calculateAllocatedRd({
              salary: m.salary,
              rdPct: m.rdPct,
              employmentType: m.employmentType,
            });
      allocatedTotal += amount || 0;
      const salary = m.salary != null && m.salary !== "" ? Number(m.salary) : NaN;
      if (Number.isFinite(salary)) salaryTotal += salary;
    });
    const unallocatedTotal = Math.max(0, salaryTotal - allocatedTotal);
    const allocatedShare = salaryTotal > 0 ? allocatedTotal / salaryTotal : 0;
    return {
      canadaCount,
      employeeCount,
      contractorCount,
      allocatedTotal,
      salaryTotal,
      unallocatedTotal,
      allocatedShare,
    };
  }, [members]);

  const filteredMembers = useMemo(() => {
    if (filter === "rd") return members.filter((m) => m.isRd);
    if (filter === "other") return members.filter((m) => !m.isRd);
    return members;
  }, [filter, members]);

  const sortedMembers = useMemo(
    () =>
      [...filteredMembers].sort((a, b) => {
        const allocatedFor = (person) => {
          const amount =
            person.allocatedRd != null
              ? person.allocatedRd
              : calculateAllocatedRd({
                  salary: person.salary,
                  rdPct: person.rdPct,
                  employmentType: person.employmentType,
                });
          return amount || 0;
        };
        const diff = allocatedFor(b) - allocatedFor(a);
        if (diff !== 0) return diff;
        return (a.name || "").localeCompare(b.name || "");
      }),
    [filteredMembers]
  );

  const teamSource = useMemo(() => {
    const orgChart = String(company?.fields?.OrgChart || "").trim();
    const linkedIn = String(company?.fields?.LinkedInUrl || "").trim();
    const sourceDomain = domain || company?.fields?.Domain || "";

    const displayUrl = (href) =>
      String(href || "")
        .replace(/^https?:\/\//i, "")
        .replace(/\/+$/, "");

    if (orgChart) {
      return {
        href: orgChart,
        display: displayUrl(orgChart),
        label: "Org chart / people URL",
        editField: "OrgChart",
        editLabel: "Edit Org Chart (Key assets)",
      };
    }
    if (linkedIn) {
      const base = linkedIn.replace(/\/+$/, "");
      const href = /\/people\/?$/i.test(base) ? `${base}/` : `${base}/people/`;
      return {
        href,
        display: displayUrl(href),
        label: "LinkedIn people",
        editField: "LinkedInUrl",
        editLabel: "Edit LinkedIn URL (General)",
      };
    }
    return {
      href: null,
      display: null,
      label: "LinkedIn people",
      editField: "LinkedInUrl",
      editLabel: "Set LinkedIn URL (General)",
      missingHint: sourceDomain
        ? `No LinkedIn URL — people are matched by domain (${sourceDomain}) only.`
        : "No LinkedIn URL set — Index team needs a company LinkedIn page.",
    };
  }, [company?.fields?.OrgChart, company?.fields?.LinkedInUrl, company?.fields?.Domain, domain]);

  const handleEditTeamSource = useCallback(() => {
    if (!teamSource?.editField) return;
    if (inlineTeamSourceEdit) {
      const currentValue =
        teamSource.editField === "OrgChart"
          ? String(company?.fields?.OrgChart || "").trim()
          : String(company?.fields?.LinkedInUrl || "").trim();
      setTeamSourceDraft(currentValue);
      setIsEditingTeamSource(true);
      return;
    }
    const sectionId =
      teamSource.editField === "OrgChart" ? "company-key-assets" : "company-general";
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    window.setTimeout(() => focusCompanyProfileField(teamSource.editField), 280);
  }, [
    company?.fields?.LinkedInUrl,
    company?.fields?.OrgChart,
    inlineTeamSourceEdit,
    teamSource?.editField,
  ]);

  const cancelEditTeamSource = useCallback(() => {
    setIsEditingTeamSource(false);
    setTeamSourceDraft("");
  }, []);

  const saveTeamSource = useCallback(async () => {
    if (!company?.id || !teamSource?.editField || isSavingTeamSource) return false;

    const field = teamSource.editField;
    const raw = String(teamSourceDraft || "").trim();
    if (!raw) {
      setActionMessage({ type: "error", text: "Enter a URL before saving." });
      return false;
    }

    let value = raw;
    if (field === "LinkedInUrl") {
      value = normalizeLinkedInCompanyUrl(raw);
      if (!isValidLinkedInCompanyUrl(value)) {
        setActionMessage({
          type: "error",
          text: "Use a LinkedIn company URL like https://www.linkedin.com/company/your-company",
        });
        return false;
      }
    } else if (!/^https?:\/\//i.test(value)) {
      value = `https://${value}`;
    }

    setIsSavingTeamSource(true);
    setActionMessage({ type: "info", text: "Saving team source URL…" });

    try {
      const updated = await patchCompanyFields(company.id, { [field]: value });
      const savedValue = updated?.fields?.[field] ?? value;
      onCompanyUpdate?.({ [field]: savedValue });
      setIsEditingTeamSource(false);
      setTeamSourceDraft("");
      setActionMessage({
        type: "success",
        text:
          field === "LinkedInUrl"
            ? `LinkedIn URL saved. Index team will use ${buildLinkedInPeopleUrl(savedValue)}`
            : "Team source URL saved.",
      });
      return true;
    } catch (error) {
      setActionMessage({ type: "error", text: error.message });
      return false;
    } finally {
      setIsSavingTeamSource(false);
    }
  }, [
    company?.id,
    isSavingTeamSource,
    onCompanyUpdate,
    teamSource?.editField,
    teamSourceDraft,
  ]);

  const companyLogoUrl = useMemo(() => {
    if (company?.fields?.LogoUrl) return company.fields.LogoUrl;
    if (sharedCompany?.logoUrl) return sharedCompany.logoUrl;
    const logoDomain = domain || company?.fields?.Domain || "";
    return logoDomain ? `https://logo.clearbit.com/${logoDomain}` : "";
  }, [company?.fields?.LogoUrl, company?.fields?.Domain, domain, sharedCompany?.logoUrl]);

  const saveStateLabel = savingId
    ? "Saving…"
    : hasUnsavedEdits
    ? "Unsaved changes"
    : "All changes saved";

  return {
    filter,
    setFilter,
    members,
    companyInCanada,
    domain,
    sharedCompany,
    isLoading,
    isRefreshing,
    isPollingForTeam,
    loadError,
    isIndexing,
    isEstimating,
    isCreating,
    savingId,
    actionMessage,
    loadMembers,
    stopTeamIndexPolling,
    handleIndexTeam,
    handleReestimate,
    handleAddPerson,
    handleFieldChange,
    handleDelete,
    allocationSummary,
    summary,
    sortedMembers,
    teamSource,
    handleEditTeamSource,
    isEditingTeamSource,
    teamSourceDraft,
    setTeamSourceDraft,
    isSavingTeamSource,
    saveTeamSource,
    cancelEditTeamSource,
    companyLogoUrl,
    saveStateLabel,
    flushEdits,
  };
}
